import { Link } from '@tanstack/react-router';
import { Package, Receipt, Ruler, Tag, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { StaffChatMessage } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

interface MoneyLike {
  amount: number;
  currency: string;
}

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;
const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const money = (value: unknown): MoneyLike | null => {
  const found = obj(value);
  return found && typeof found.amount === 'number' && typeof found.currency === 'string'
    ? { amount: found.amount, currency: found.currency }
    : null;
};
const list = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value)
    ? value.flatMap((item) => (obj(item) ? [item as Record<string, unknown>] : []))
    : [];

function Frame({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-1 text-xs">
      <p className="flex items-center gap-1.5 font-medium">
        <span aria-hidden className="shrink-0">
          {icon}
        </span>
        {title}
      </p>
      {children}
    </div>
  );
}

/**
 * The jewellery cards (LC12) as staff see them: the same payload the shopper's widget draws,
 * product cards priced in the shopper's market.
 */
export function JewelleryCard({
  message,
  storeId,
}: {
  message: StaffChatMessage;
  storeId: string;
}) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const payload = message.payload;
  switch (message.kind) {
    case 'product': {
      const product = obj(payload.product);
      const variant = obj(payload.variant);
      const price = money(variant?.price) ?? money(obj(product?.price)?.min);
      return (
        <Frame
          icon={<Package className="size-3.5" />}
          title={str(product?.title) ?? t('message.productCard')}
        >
          {product ? (
            <p className="opacity-80">
              {[str(variant?.title), price ? format.money(price) : null]
                .filter(Boolean)
                .join(' · ')}
              {payload.needsOptions === true ? ` · ${t('jewellery.needsOptions')}` : ''}
            </p>
          ) : (
            <p className="opacity-80">{t('jewellery.unpublished')}</p>
          )}
        </Frame>
      );
    }
    case 'order_status': {
      const stage = obj(payload.productionStage);
      const shipByAt = str(payload.shipByAt);
      const orderId = str(payload.orderId);
      return (
        <Frame
          icon={<Truck className="size-3.5" />}
          title={t('jewellery.orderCard', { name: str(payload.orderName) ?? '' })}
        >
          <p className="opacity-80">
            {[
              str(payload.paymentStatus)
                ? t(`jewellery.payment.${str(payload.paymentStatus)}`, {
                    defaultValue: str(payload.paymentStatus),
                  })
                : null,
              stage ? t('jewellery.stage', { name: str(stage.name) ?? '' }) : null,
              shipByAt ? t('jewellery.shipBy', { date: format.date(shipByAt) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {orderId ? (
            <Link
              to="/s/$storeId/orders/$orderId"
              params={{ storeId, orderId }}
              className="underline underline-offset-2"
            >
              {t('jewellery.openOrder')}
            </Link>
          ) : null}
        </Frame>
      );
    }
    case 'discount': {
      const endsAt = str(payload.endsAt);
      return (
        <Frame
          icon={<Tag className="size-3.5" />}
          title={t('jewellery.discountCard', { code: str(payload.code) ?? '' })}
        >
          <p className="opacity-80">
            {[
              str(payload.title),
              str(payload.summary),
              endsAt ? t('jewellery.until', { date: format.date(endsAt) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </Frame>
      );
    }
    case 'invoice': {
      const total = money(payload.total);
      const draftId = str(payload.draftOrderId);
      return (
        <Frame
          icon={<Receipt className="size-3.5" />}
          title={t('jewellery.invoiceCard', { name: str(payload.name) ?? '' })}
        >
          <ul className="opacity-80">
            {list(payload.lines).map((line, index) => (
              <li key={index}>
                {line.quantity as number} × {str(line.title)}
              </li>
            ))}
          </ul>
          <p>
            {total ? format.money(total) : null}
            {str(payload.email) ? ` · ${t('jewellery.sentTo', { email: str(payload.email) })}` : ''}
          </p>
          {draftId ? (
            <Link
              to="/s/$storeId/draft-orders/$draftId"
              params={{ storeId, draftId }}
              className="underline underline-offset-2"
            >
              {t('jewellery.openQuote')}
            </Link>
          ) : null}
        </Frame>
      );
    }
    case 'size_guide': {
      const sizes = list(payload.sizes);
      return (
        <Frame icon={<Ruler className="size-3.5" />} title={t('jewellery.sizeGuideCard')}>
          <p className="opacity-80">
            {[
              sizes.length > 0 ? t('jewellery.sizeCount', { count: sizes.length }) : null,
              str(payload.sizeGuideHandle) ? `/pages/${str(payload.sizeGuideHandle)}` : null,
              payload.freeSizer === true ? t('jewellery.freeSizer') : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </Frame>
      );
    }
    case 'text':
    case 'file':
    case 'form':
    case 'event':
      return null;
  }
}
